import {
  ItemResponse,
  PageRequest,
  PageResponse,
  Response,
  WriteResponse,
} from "@/types/api-types";
import { MateEmotion } from "@/types/chat";
import { get, post, put, deleteOne, fetchStream } from "@/modules/request";
import {
  MateAdoption,
  MateWish,
  AdoptionFavorite,
  MateMemory,
  MateMemorySlot,
  Mate,
} from "@/types/entity";
import {
  AdoptionStatus,
  DriftCapsuleRarity,
  MateType,
  MemoryCapacityLevel,
  MemoryType,
} from "@/enums/mateEnums";
import { MateCardFactoryGenerateType } from "@/enums/chatEnums";
import { DriftCapsule, DriftCategory } from "@/types/mate";

// Mate Emotion
export function readMateEmotion(
  params: PageRequest<{ mateId: number }>
): Promise<ItemResponse<MateEmotion>> {
  return get("/MateEmotion/ReadAll", params);
}

export function createOneMateEmotion(
  params: Pick<MateEmotion, "name" | "mediaId" | "isNsfw">
): Promise<WriteResponse<MateEmotion>> {
  return post("/MateEmotion", params);
}

export function createManyMateEmotion(): Promise<ItemResponse<void>> {
  return post("/MateEmotion/CreateMany");
}

export function updateOneMateEmotion(
  id: number,
  params: Pick<MateEmotion, "name" | "mediaId" | "isNsfw">
): Promise<WriteResponse<MateEmotion>> {
  return put(`/MateEmotion/${id}`, params);
}

export function deleteOneMateEmotion(id: number): Promise<WriteResponse<null>> {
  return deleteOne(`/MateEmotion/${id}`);
}

// Mate Wish

export function createOneMateWish(
  params: Partial<MateWish>
): Promise<WriteResponse<MateWish>> {
  return post("/MateWish", params);
}

export function readOneMateWish(
  uuid: string
): Promise<WriteResponse<MateWish>> {
  return get(`/MateWish/${uuid}`);
}

export function readMateWishPage(
  params: PageRequest<{ status?: number }>
): Promise<PageResponse<MateWish>> {
  return get("/MateWishPage", params);
}

// Mate Adoption

export function createOneMateAdoption(
  params: Partial<MateAdoption>
): Promise<WriteResponse<MateAdoption>> {
  return post("/MateAdoption", params);
}

export function readOneMateAdoption(
  uuid: string
): Promise<WriteResponse<MateAdoption>> {
  return get(`/MateAdoption/${uuid}`);
}

export function updateOneMateAdoption(
  id: number,
  params: {
    title?: string;
    powerCubes?: number;
    isAiGenerated?: boolean;
    status?: AdoptionStatus;
  }
): Promise<WriteResponse<MateAdoption>> {
  return put(`/MateAdoption/${id}`, params);
}

export function deleteOneMateAdoption(
  id: number
): Promise<WriteResponse<null>> {
  return deleteOne(`/MateAdoption/${id}`);
}

export function readMateAdoptionPage(
  params: PageRequest<{ role: "publisher" | "adopter"; status: AdoptionStatus }>
): Promise<PageResponse<MateAdoption>> {
  return get("/MateAdoptionPage", params);
}

export function readMateAdoptionExplorePage(
  params: PageRequest<{ status: AdoptionStatus; mateType?: MateType }>
): Promise<PageResponse<MateAdoption>> {
  return get("/MateAdoptionExplorePage", params);
}

export function transferOneMateAdoption(id: number): Promise<
  Response<{
    mate: Mate;
    item: MateAdoption;
  }>
> {
  return post(`/MateAdoptionTransfer/${id}`);
}

// Adoption Favorite
export function createOneAdoptionFavorite(
  adoptionId: number
): Promise<WriteResponse<AdoptionFavorite>> {
  return post("/AdoptionFavorite", { adoptionId });
}

export function deleteOneAdoptionFavorite(id: number) {
  return deleteOne("/AdoptionFavorite?adoptionId=" + id);
}

export function readAdoptionFavoritePage(
  params: PageRequest<{ status: AdoptionStatus }>
): Promise<PageResponse<MateAdoption>> {
  return get("/AdoptionFavoritePage", params);
}

export function ApiPostReplyCompleteSteam(
  params: {
    entityId: number;
    entityType: string;
    parentId: number;
    mateId: number;
    content: string;
    type: MateCardFactoryGenerateType;
    journalIdzz?: number[];
    mailIdzz?: number[];
    dispatchIdzz?: number[];
    memoryIdzz?: number[];
  },
  cb: (content: Response<{ text: string }> | string) => void
) {
  return fetchStream(`/ReplyComplete`, "POST", params, cb);
}

export function createOneMateMemory(params: {
  mateId: number;
  name: string;
  text?: string;
  labelColor: string;
  isAutoExtracted: boolean;
  capacityLevel: MemoryCapacityLevel;
  type: MemoryType;

  extractionSource?: {
    profile: boolean;
    mailIds?: number[];
    momentIds?: number[];
    roleplayAnswerIds?: number[];
    dispatchIds?: number[];
    journalIds?: number[];
  };
}): Promise<WriteResponse<MateMemory>> {
  return post("/MateMemory", params);
}

export function reExtractOneMateMemory(
  id: number
): Promise<WriteResponse<MateMemory>> {
  return post(`/MateMemoryReExtract/${id}`);
}

export function updateOneMateMemory(
  id: number,
  params: Partial<MateMemory>
): Promise<WriteResponse<MateMemory>> {
  return put(`/MateMemory/${id}`, params);
}

export function loadOneMateMemory(
  id: number
): Promise<WriteResponse<MateMemory>> {
  return post(`/MateMemoryLoad/${id}`);
}

export function deleteOneMateMemory(id: number): Promise<WriteResponse<null>> {
  return deleteOne(`/MateMemory/${id}`);
}

export function readMateMemoryPage(
  params: PageRequest<{
    mateId: number;
    type: MemoryType;
    capacityLevel?: MemoryCapacityLevel;
  }>
): Promise<PageResponse<MateMemory>> {
  return get("/MateMemoryPage", params);
}

export function readMateMemorySlotPage(
  params: PageRequest<{
    mateId: number;
    capacityLevel?: MemoryCapacityLevel;
  }>
): Promise<PageResponse<MateMemorySlot>> {
  return get("/MateMemorySlotPage", params);
}

export function purchaseOneMateMemorySlot(params: {
  mateId: number;
}): Promise<WriteResponse<MateMemorySlot>> {
  return post("/MateMemorySlotPurchase", params);
}

export function unloadOneMateMemorySlot(
  id: number
): Promise<WriteResponse<MateMemorySlot>> {
  return put(`/MateMemorySlot/${id}`, {
    memoryId: null,
  });
}

export type DriftCategoryType = "all" | "salvageable" | "nonSalvageable";

export function readDriftCategoryPage(
  params: PageRequest<{
    type: DriftCategoryType;
  }>
): Promise<PageResponse<DriftCategory>> {
  return get("/DriftCategoryPage", params);
}

export function readDriftCapsulePage(
  params: PageRequest<{
    categoryId?: number;
    rarity?: DriftCapsuleRarity;
  }>
): Promise<PageResponse<DriftCapsule>> {
  return get("/DriftCapsulePage", params);
}

// Route::post('/DriftCapsuleSalvage/{id}', \App\Api\DriftCapsule\SalvageOne\SalvageOneDriftCapsuleController::class)->middleware(['auth']);
export function salvageOneDriftCapsule(
  categoryId: number
): Promise<WriteResponse<DriftCapsule>> {
  return post(`/DriftCapsuleSalvage`, { categoryId });
}
